/* eslint-disable max-lines */
/**
 * FrameBuffer — decode-ahead sliding window for one track, modelled on a
 * `<video>`'s forward/back buffer.
 *
 * In the shipped path this is a **decode-ahead cache-warmer + readiness oracle**,
 * not the presentation layer: it prefetches the window around the playhead via
 * `img.decode()` (nearest-first, concurrency-capped) so that when the real <img>
 * (rendered by FramePlayer) points at a frame, it paints from the browser image
 * cache with no flash. Callers read `get(i)` only to ask "is frame i decoded yet?"
 * — the decoded HTMLImageElement doubles as that ready signal. Eviction keeps the
 * warmed set bounded.
 *
 * A frame is never permanently abandoned: a failed decode (bad/expired URL, or a
 * network cancellation on a slow connection) is retried after a cooldown, so a
 * valid-but-slow frame always eventually loads. Deciding to SKIP a genuinely stuck
 * frame lives in the view (FramesMedia), keyed off how long the playhead has really
 * been stalled — the reliable signal — not a decode-failure count that a flaky 3G
 * connection trips on healthy frames.
 * ponytail: swap the decode fn for a worker + ImageBitmap/canvas, or paint the
 * decoded element directly, if main-thread decode jank ever shows up.
 */

import { DecodedFrame, DecodeFn, Frame } from './types';

/** Default decoder: download + decode an <img> off the visible tree. */
export const defaultDecode: DecodeFn = (url, signal) => {
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  if (signal) {
    signal.addEventListener('abort', () => {
      img.src = '';
    });
  }
  // decode() resolves once the bitmap is ready to paint with no flash.
  return img.decode().then(() => img);
};

interface Options {
  forward?: number; // frames ahead of the playhead to keep decoded
  back?: number; // frames behind to keep decoded
  maxConcurrent?: number; // simultaneous in-flight decodes
  cooldownMs?: number; // wait this long before retrying a failed frame
  now?: () => number; // injectable clock (ms) for the retry cooldown
  decode?: DecodeFn;
}

export class FrameBuffer {
  private readonly forward: number;
  private readonly back: number;
  private readonly maxConcurrent: number;
  private readonly cooldownMs: number;
  private readonly now: () => number;
  private readonly decode: DecodeFn;

  private readonly decoded = new Map<number, DecodedFrame>();
  private readonly inflight = new Map<number, AbortController>();
  // index -> wall time before which we won't retry it. Set on a genuine (non-abort)
  // decode failure so a bad URL is retried at most once per cooldown (no hammering)
  // without ever being permanently abandoned.
  private readonly retryAfter = new Map<number, number>();
  private queue: number[] = [];
  private disposed = false;

  constructor(private frames: Frame[], opts: Options = {}) {
    this.forward = opts.forward ?? 12;
    this.back = opts.back ?? 8;
    this.maxConcurrent = opts.maxConcurrent ?? 4;
    this.cooldownMs = opts.cooldownMs ?? 1500;
    this.now = opts.now ?? (() => performance.now());
    this.decode = opts.decode ?? defaultDecode;
  }

  /** A decoded image for `index`, or undefined if not ready yet. */
  get(index: number): DecodedFrame | undefined {
    return this.decoded.get(index);
  }

  /**
   * Re-centre the window on `cursor`: prefetch the window nearest-first and evict
   * everything outside it. Cheap to call every animation frame.
   */
  update(cursor: number): void {
    if (this.disposed || this.frames.length === 0) return;
    const lo = Math.max(0, cursor - this.back);
    const hi = Math.min(this.frames.length - 1, cursor + this.forward);

    // Evict everything outside [lo, hi], including retry cooldowns so a frame that
    // scrolls out and back gets a fresh attempt immediately.
    for (const i of this.decoded.keys()) {
      if (i < lo || i > hi) this.decoded.delete(i);
    }
    for (const [i, ctrl] of this.inflight) {
      if (i < lo || i > hi) {
        ctrl.abort();
        this.inflight.delete(i);
      }
    }
    for (const i of this.retryAfter.keys()) {
      if (i < lo || i > hi) this.retryAfter.delete(i);
    }

    // Build a prefetch queue ordered by distance from the cursor (playhead first).
    const wanted: number[] = [];
    for (let d = 0; lo + d <= hi || cursor - d >= lo; d++) {
      const ahead = cursor + d;
      const behind = cursor - d;
      if (ahead <= hi && !this.has(ahead)) wanted.push(ahead);
      if (d > 0 && behind >= lo && !this.has(behind)) wanted.push(behind);
    }
    this.queue = wanted;
    this.pump();
  }

  private has(index: number): boolean {
    return (
      this.decoded.has(index) ||
      this.inflight.has(index) ||
      this.now() < (this.retryAfter.get(index) ?? 0) // cooling down after a failure
    );
  }

  private pump(): void {
    while (this.inflight.size < this.maxConcurrent && this.queue.length > 0) {
      const index = this.queue.shift();
      if (index === undefined || this.has(index)) continue;
      const frame = this.frames[index];
      if (!frame) continue;
      const ctrl = new AbortController();
      this.inflight.set(index, ctrl);
      // Guard mutations with `inflight.get(index) === ctrl`: an earlier decode for
      // the same index (aborted, then re-queued) can settle late and must not clobber
      // the current attempt's inflight entry or record a false failure.
      this.decode(frame.url, ctrl.signal)
        .then((img) => {
          if (this.inflight.get(index) !== ctrl) return this.pump();
          this.inflight.delete(index);
          if (!this.disposed && !ctrl.signal.aborted) {
            this.decoded.set(index, img);
            this.retryAfter.delete(index);
          }
          this.pump();
        })
        .catch(() => {
          if (this.inflight.get(index) !== ctrl) return this.pump();
          this.inflight.delete(index);
          // Only a genuine failure (not our eviction abort) earns a cooldown.
          if (!ctrl.signal.aborted) {
            this.retryAfter.set(index, this.now() + this.cooldownMs);
          }
          this.pump();
        });
    }
  }

  dispose(): void {
    this.disposed = true;
    this.inflight.forEach((ctrl) => ctrl.abort());
    this.inflight.clear();
    this.decoded.clear();
    this.retryAfter.clear();
    this.queue = [];
  }
}
